import { Alert, Box, Card, CardContent, CardHeader, Checkbox, FormControlLabel, Grid, Stack, Switch, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  ListBase,
  SaveButton,
  SelectInput,
  SimpleForm,
  Toolbar,
  useNotify,
  usePermissions,
  useTranslate,
  useUpdate,
  WithListContext,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { XDateTimeInput } from '../../components/XDateTimeInput';
import dayjs from 'dayjs';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { Iconify } from 'src/components/iconify';
import AuditLogDialog from '../../components/AuditLog/AuditLogDialog';
import { Resources } from '../resource';
import { PermissionData } from '../../entities/permission.entity';
import { hasPermission } from '../../utils/has-permission';

interface MaintenanceData {
  id: string;
  isMaintainSchedule: boolean;
  maintenanceFeatures?: string[];
  warningAt: string | null;
  from: string | null;
  to: string | null;
  createAt: string;
  updateAt: string | null;
}

const ALL_MAINTENANCE_FEATURES = ['PREDICTION_TRADING', 'TAP_GRID'];

const MaintenanceForm = ({ data, isLoading }: { data: any; isLoading: any }) => {
  const translate = useTranslate();
  const [update] = useUpdate();
  const notify = useNotify();
  const [isMaintenanceEnabled, setIsMaintenanceEnabled] = useState(false);
  const [maintenanceData, setMaintenanceData] = useState<MaintenanceData | null>(null);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const maintenanceCheckedRef = useRef(false);
  const { data: permissionData } = usePermissions<PermissionData>();
  const { permissions = [] } = permissionData || {};
  const canUpdateMaintenance = hasPermission(permissions, 'maintenance', 'update');

  const warningTimeChoices = [
    { id: 5, name: translate('module.maintenance.warning.5_minutes') },
    { id: 10, name: translate('module.maintenance.warning.10_minutes') },
    { id: 15, name: translate('module.maintenance.warning.15_minutes') },
    { id: 30, name: translate('module.maintenance.warning.30_minutes') },
    { id: 60, name: translate('module.maintenance.warning.1_hour') },
    { id: 120, name: translate('module.maintenance.warning.2_hours') },
    { id: 360, name: translate('module.maintenance.warning.6_hours') },
    { id: 720, name: translate('module.maintenance.warning.12_hours') },
    { id: 1440, name: translate('module.maintenance.warning.1_day') },
    { id: 2880, name: translate('module.maintenance.warning.2_days') },
    { id: 4320, name: translate('module.maintenance.warning.3_days') },
    { id: 10080, name: translate('module.maintenance.warning.1_week') },
  ];

  const autoDisableMaintenance = useCallback(
    async (maintenanceRecord: MaintenanceData) => {
      // PUT /system/maintenance/:id requires maintenance:update — a read-only viewer must not fire it.
      if (!canUpdateMaintenance) return;
      try {
        await update('system/maintenance', {
          id: 'singleton',
          data: {
            isMaintainSchedule: false,
            maintenanceFeatures: maintenanceRecord.maintenanceFeatures || [],
            warningAt: maintenanceRecord.warningAt,
            from: maintenanceRecord.from,
            to: maintenanceRecord.to,
          },
        });
        setIsMaintenanceEnabled(false);
      } catch (error) {
        console.error('Failed to auto-disable maintenance:', error);
      }
    },
    [update, canUpdateMaintenance]
  );

  useEffect(() => {
    if (data && data.length > 0) {
      const maintenance = data[0];
      setMaintenanceData(maintenance);
      setIsMaintenanceEnabled(maintenance.isMaintainSchedule);
      if (maintenance.maintenanceFeatures && maintenance.maintenanceFeatures.length > 0) {
        setSelectedFeatures(maintenance.maintenanceFeatures);
      }
      if (maintenance.to && maintenance.isMaintainSchedule && !maintenanceCheckedRef.current) {
        const endTime = new Date(maintenance.to);
        const now = new Date();
        if (now > endTime) {
          autoDisableMaintenance(maintenance);
          maintenanceCheckedRef.current = true;
        }
      }
    }
  }, [data, autoDisableMaintenance]);

  const getWarningMinutes = () => {
    if (!maintenanceData?.warningAt || !maintenanceData?.from) {
      return 30;
    }
    const warningTime = new Date(maintenanceData.warningAt);
    const startTime = new Date(maintenanceData.from);
    const diffMs = startTime.getTime() - warningTime.getTime();
    const diffMinutes = Math.round(diffMs / (1000 * 60));

    return diffMinutes > 0 ? diffMinutes : 30;
  };

  const defaultValues = maintenanceData
    ? {
        warningMinutes: getWarningMinutes(),
        from: maintenanceData.from,
        to: maintenanceData.to,
      }
    : {
        warningMinutes: 30,
        from: null,
        to: null,
      };

  const handleMaintenanceToggle = async (enabled: boolean) => {
    try {
      const payload = {
        isMaintainSchedule: enabled,
        maintenanceFeatures: selectedFeatures,
        warningAt: maintenanceData?.from && getWarningMinutes() > 0 ? new Date(new Date(maintenanceData.from).getTime() - getWarningMinutes() * 60 * 1000).toISOString() : null,
        from: maintenanceData?.from,
        to: maintenanceData?.to,
      };

      await update('system/maintenance', {
        id: 'singleton',
        data: payload,
      });

      notify(
        translate(
          enabled
            ? 'module.maintenance.messages.enabled_success'
            : 'module.maintenance.messages.disabled_success'
        ),
        {
          type: 'success',
          multiLine: true,
        }
      );
    } catch (error) {
      notify(translate('module.maintenance.messages.update_failed'), {
        type: 'error',
        multiLine: true,
      });
    }
  };

  const handleFormSubmit = async (formData: any) => {
    try {
      let warningAt = null;
      if (formData.from && formData.warningMinutes > 0) {
        const startTime = new Date(formData.from);
        const endTime = new Date(formData.to);

        if (startTime >= endTime) {
          notify(translate('module.maintenance.messages.invalid_time_range'), {
            type: 'error',
            multiLine: true,
          });
          return;
        }

        const now = new Date();
        if (startTime <= now) {
          notify(translate('module.maintenance.messages.start_time_past'), {
            type: 'error',
            multiLine: true,
          });
          return;
        }

        if (!isNaN(startTime.getTime())) {
          warningAt = new Date(startTime.getTime() - formData.warningMinutes * 60 * 1000);
        }
      }
      const payload = {
        isMaintainSchedule: isMaintenanceEnabled,
        maintenanceFeatures: selectedFeatures,
        warningAt: warningAt && !isNaN(warningAt.getTime()) ? warningAt.toISOString() : null,
        from: formData.from,
        to: formData.to,
      };
      await update('system/maintenance', {
        id: 'singleton',
        data: payload,
      });
      notify(translate('module.maintenance.messages.config_success'), {
        type: 'success',
        multiLine: true,
      });
    } catch (error) {
      notify(translate('module.maintenance.messages.config_failed'), {
        type: 'error',
        multiLine: true,
      });
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        {translate('common.loading')}
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardHeader title={translate('module.maintenance.control.title')} />
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: isMaintenanceEnabled ? 'warning.lighter' : 'background.paper',
              }}
            >
              <Box>
                <Typography variant="h6">
                  {translate('module.maintenance.control.schedule_title')}
                </Typography>{' '}
                <Typography variant="body2" color="text.secondary">
                  {translate(
                    isMaintenanceEnabled
                      ? 'module.maintenance.control.status_active'
                      : 'module.maintenance.control.status_disabled'
                  )}
                </Typography>
              </Box>
              <Switch
                checked={isMaintenanceEnabled}
                onChange={(e) => handleMaintenanceToggle(e.target.checked)}
                disabled={!canUpdateMaintenance}
                color="warning"
              />
            </Box>

            {isMaintenanceEnabled && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {translate('module.maintenance.control.enabled_info')}
              </Alert>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader title={translate('module.maintenance.config.maintenanceFeatures')} />
          <CardContent>
            <Stack direction="row" spacing={4}>
              {ALL_MAINTENANCE_FEATURES.map((feature) => (
                <FormControlLabel
                  key={feature}
                  control={
                    <Checkbox
                      checked={selectedFeatures.includes(feature)}
                      onChange={(e) => {
                        setSelectedFeatures((prev) =>
                          e.target.checked
                            ? [...prev, feature]
                            : prev.filter((f) => f !== feature)
                        );
                      }}
                      color="warning"
                    />
                  }
                  label={feature}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={translate('module.maintenance.config.title')}
            subheader={translate('module.maintenance.config.subtitle')}
          />
          <CardContent>
            <SimpleForm
              key={maintenanceData?.id}
              defaultValues={defaultValues}
              onSubmit={handleFormSubmit}
              toolbar={
                <Toolbar>{canUpdateMaintenance && <SaveButton />}</Toolbar>
              }
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <SelectInput
                    source="warningMinutes"
                    label={translate('module.maintenance.config.warning_time')}
                    choices={warningTimeChoices}
                    helperText={translate('module.maintenance.config.warning_time_help')}
                    fullWidth
                    sx={{ margin: 0 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <XDateTimeInput
                    source="from"
                    label={translate('module.maintenance.config.start_time')}
                    helperText={translate('module.maintenance.config.start_time_help')}
                    fullWidth
                    minDate={dayjs()}
                    maxDate={dayjs('2099-12-31')}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <XDateTimeInput
                    source="to"
                    label={translate('module.maintenance.config.end_time')}
                    helperText={translate('module.maintenance.config.end_time_help')}
                    fullWidth
                    minDate={dayjs()}
                    maxDate={dayjs('2099-12-31')}
                  />
                </Grid>
              </Grid>
            </SimpleForm>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const View = () => {
  const translate = useTranslate();
  const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [auditTitle, setAuditTitle] = useState('Audit Logs');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);

  return (
    <DashboardContent>
      <CustomBreadcrumbs
        heading={translate('module.maintenance.title')}
        action={
          <Stack>
            <PermissionGuard resource="audit-logs" action="read" fallback={<></>} loadingFallback={<></>}>
              <Button
                variant="contained"
                size="medium"
                color="secondary"
                onClick={() => {
                  setSelectedRecord({});
                  setAuditTitle(`${translate('resource.auditLogs')} - ${translate('module.maintenance.title')}`);
                  setAuditLogOpen(true);
                }}
                label={translate('resource.auditLogs')}
              >
                <Iconify icon="ph:note" />
              </Button>
            </PermissionGuard>
          </Stack>
        }
      />
      <ListBase resource="system/maintenance">
        <WithListContext
          render={({ data, isLoading }) => {
            return <MaintenanceForm data={data} isLoading={isLoading} />;
          }}
        />
      </ListBase>
      <AuditLogDialog
        open={auditLogOpen}
        onClose={() => setAuditLogOpen(false)}
        recordId={selectedRecord?.id}
        resource={Resources.maintenance}
        title={auditTitle}
      />
    </DashboardContent>
  );
};

export default View;
